import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import {
  BellAlertIcon,
  FunnelIcon,
  SparklesIcon,
  GlobeAltIcon,
  ShieldCheckIcon,
  BoltIcon,
} from '@heroicons/react/24/outline';

gsap.registerPlugin(ScrollTrigger);

const Features = () => {
  const sectionRef = useRef(null);
  const headerRef = useRef(null);
  const cardsRef = useRef(null);

  const features = [
    {
      icon: BellAlertIcon,
      title: 'Real-Time Alerts',
      description: 'Get instant notifications when new commits are pushed to your watched GitHub or GitLab repositories.',
      color: 'from-accent-500 to-emerald-500',
    },
    {
      icon: SparklesIcon,
      title: 'Smart Priority',
      description: 'AI-powered filtering ensures you only see commits that matter to your workflow.',
      color: 'from-blue-500 to-cyan-500',
    },
    {
      icon: BoltIcon,
      title: 'Red Badge Counter',
      description: 'Instantly see how many unread commits await with the iconic red badge.',
      color: 'from-red-500 to-orange-500',
    },
    {
      icon: FunnelIcon,
      title: 'Repo Filters',
      description: 'Fine-tune which repositories and branches trigger notifications.',
      color: 'from-purple-500 to-pink-500',
    },
    {
      icon: GlobeAltIcon,
      title: 'Chrome & Edge',
      description: 'Works seamlessly on Chromium browsers. Firefox support coming soon.',
      color: 'from-yellow-500 to-amber-500',
    },
    {
      icon: ShieldCheckIcon,
      title: 'Privacy First',
      description: 'No tracking, no analytics. Your data stays between you and your GitHub or GitLab account.',
      color: 'from-teal-500 to-green-500',
    },
  ];

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Header animation
      gsap.fromTo(headerRef.current?.children || [],
        { y: 60, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 1,
          stagger: 0.15,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: headerRef.current,
            start: 'top 80%',
            end: 'top 50%',
            toggleActions: 'play none none reverse',
          },
        }
      );

      // Cards staggered animation with 3D effect
      const cards = cardsRef.current?.children || [];
      gsap.fromTo(cards,
        { 
          y: 100, 
          opacity: 0, 
          rotateX: 15,
          scale: 0.9
        },
        {
          y: 0,
          opacity: 1,
          rotateX: 0,
          scale: 1,
          duration: 0.8,
          stagger: {
            each: 0.1,
            from: 'start',
          },
          ease: 'power3.out',
          scrollTrigger: {
            trigger: cardsRef.current,
            start: 'top 85%',
            end: 'top 50%',
            toggleActions: 'play none none reverse',
          },
        }
      );

      // Parallax background orb
      gsap.to('.features-orb', {
        y: -100,
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top bottom',
          end: 'bottom top',
          scrub: 1,
        },
      });

    }, sectionRef);

    return () => ctx.revert();
  }, []);

  // Simple hover effect for cards - no expensive mouse tracking
  const handleCardEnter = (e) => {
    const card = e.currentTarget;
    gsap.to(card, {
      scale: 1.01,
      y: -6,
      duration: 0.3,
      ease: 'power2.out',
    });
  };

  const handleCardLeave = (e) => {
    const card = e.currentTarget;
    gsap.to(card, {
      scale: 1,
      y: 0,
      duration: 0.3,
      ease: 'power2.out',
    });
  };

  return (
    <section
      ref={sectionRef}
      className="relative py-16 md:py-20 overflow-hidden"
      id="features"
    >
      {/* Background elements */}
      <div className="absolute inset-0 bg-dark-950" />
      <div className="features-orb absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-accent-500/5 rounded-full blur-[150px]" />
      
      {/* Animated lines */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-0 w-full h-px bg-gradient-to-r from-transparent via-accent-500/20 to-transparent" />
        <div className="absolute bottom-1/4 left-0 w-full h-px bg-gradient-to-r from-transparent via-blue-500/20 to-transparent" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6">
        {/* Section header */}
        <div ref={headerRef} className="text-center mb-12 sm:mb-16 md:mb-20">
          <span className="inline-block px-4 sm:px-5 py-2 mb-6 sm:mb-8 text-xs sm:text-sm font-semibold text-accent-400 bg-accent-500/10 rounded-full border border-accent-500/20 tracking-wide uppercase">
            Features
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 sm:mb-6 leading-tight px-2">
            Everything You Need,{' '}
            <br className="hidden sm:block" />
            <span className="gradient-text">Nothing You Don't</span>
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-dark-400 max-w-3xl mx-auto leading-relaxed px-2">
            Built by developers, for developers. Every feature is designed to
            enhance your workflow without getting in the way.
          </p>
        </div>

        {/* Feature cards */}
        <div
          ref={cardsRef}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8"
          style={{ perspective: '1000px' }}
        >
          {features.map((feature, index) => (
            <div
              key={index}
              className="group relative p-6 sm:p-8 rounded-2xl sm:rounded-3xl glass border border-white/5 hover:border-accent-500/30 transition-colors duration-500"
              onMouseEnter={handleCardEnter}
              onMouseLeave={handleCardLeave}
            >
              {/* Glow effect that follows cursor */}
              <div className="card-glow absolute w-40 h-40 bg-accent-500/20 rounded-full blur-[60px] opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }} />
              
              {/* Animated border gradient */}
              <div className="absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 overflow-hidden">
                <div className="absolute inset-[-2px] bg-gradient-to-r from-accent-500/50 via-blue-500/50 to-purple-500/50 rounded-3xl blur-sm animate-gradient bg-300%" />
              </div>

              <div className="relative z-10">
                {/* Icon */}
                <div className={`w-12 h-12 sm:w-14 sm:h-14 mb-4 sm:mb-6 rounded-xl sm:rounded-2xl bg-gradient-to-br ${feature.color} p-[1px] transform group-hover:scale-110 transition-transform duration-500`} style={{ transform: 'translateZ(20px)' }}>
                  <div className="w-full h-full bg-dark-900 rounded-xl sm:rounded-2xl flex items-center justify-center group-hover:bg-dark-800 transition-colors">
                    <feature.icon className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                  </div>
                </div>

                {/* Content */}
                <h3 className="text-xl sm:text-2xl font-bold text-white mb-2 sm:mb-3 group-hover:text-accent-400 transition-colors duration-300" style={{ transform: 'translateZ(15px)' }}>
                  {feature.title}
                </h3>
                <p className="text-dark-400 leading-relaxed text-base sm:text-lg" style={{ transform: 'translateZ(10px)' }}>
                  {feature.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Features;
